import 'intersection-observer';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import '@reach/tabs/styles.css';
import '@reach/menu-button/styles.css';
import '@reach/tooltip/styles.css';
import { API_HOST, REDIRECT_URL } from 'appConstants';
import Loading from 'components/Loading';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import { getSessionId, redirectToAuth } from './helpers';
import { getCredentials, setTheme } from './store/actions';
import Editor from './routes/Editor';
import NotFound from './routes/Home/NotFound';
import Home from './routes/Home';
import './App.sass';

// configuring axios
axios.defaults.baseURL = API_HOST;

axios.interceptors.response.use(null, (error) => {
  if (error?.response?.status === 401) {
    redirectToAuth();
  }

  return Promise.reject(error);
});
// configuring react-toastify
toast.configure();
// configuring react-modal (http://reactcommunity.org/react-modal/accessibility/)
Modal.setAppElement('#root');

function App() {
  const { theme } = useSelector((state) => state.user);
  const subscriptionPlanNumber = useSelector(
    (state) => state.user?.subscriptionPlan?.subscriptionPlanNumber,
  );
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [loaded, setLoaded] = useState(false);
  const [showAlertInfo, setShowAlertInfo] = useState(true);
  const [newSessionId, setNewSessionId] = useState('');
  const handleSetTheme = useCallback(
    (theme) => {
      dispatch(setTheme({ theme }));

      if (theme === 'dark') {
        document.body.classList.add('general-dark-theme');
        document.body.classList.remove('general-light-theme');
      } else {
        document.body.classList.remove('general-dark-theme');
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const sessionId = getSessionId();

    if (newSessionId) {
      setLoaded(false);
    }

    const requestCredentials = async () => {
      try {
        await dispatch(getCredentials(newSessionId || sessionId));
      } catch {
        // error handling is done by the thunk / axios interceptor
      } finally {
        setLoaded(true);
      }
    };

    if (
      pathname === '/not-found' ||
      window.location.pathname === '/not-found'
    ) {
      setLoaded(true);

      return false;
    }

    if (!sessionId) {
      return redirectToAuth();
    }

    requestCredentials();
  }, [dispatch, newSessionId, pathname]);

  let themeClass = `${theme}-theme`;

  return loaded ? (
    <div id="layout" className={themeClass}>
      {showAlertInfo && subscriptionPlanNumber === 0 && (
        <div className="alert-info" role="alert">
          <div className="alert-info__content">
            <span className="icon icon-info" />
            <span>
              You are currenlty trying out our Premium Features. Your Free Trial
              ends soon!
            </span>
            <button
              onClick={() =>
                window.open(`${REDIRECT_URL}/settings/subscriptions`)
              }>
              Upgrade Now
            </button>
          </div>
          <span
            className="icon icon-close"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setShowAlertInfo(false);
              }
            }}
            onClick={() => setShowAlertInfo(false)}
          />
        </div>
      )}
      <Switch>
        <Route path="/edit/:id">
          <Editor />
        </Route>
        <Route path="/" exact>
          <Home
            setNewSessionId={setNewSessionId}
            handleSetTheme={handleSetTheme}
            theme={theme}
          />
        </Route>
        <Route path="/not-found">
          <NotFound />
        </Route>
        <Redirect to="/" />
      </Switch>
    </div>
  ) : (
    <Loading />
  );
}

export default App;
